import useStore from 'state/knovStore'
import type { GlobalNoticeVariant } from 'state/knovStore'

/**
 * Thin facade over the app's GlobalNotice toast system (state/knovStore + GlobalNotice.tsx),
 * usable from any call site (event handlers, ws callbacks, api.ts) without repeating
 * useStore.getState() boilerplate.
 *
 * Lifecycle pattern for async work (e.g. a payment send):
 *   const kind = `payment-${paymentId}`
 *   toast.pending('Sending payment…', { kind })   // spinner, no auto-dismiss
 *   ...later...
 *   toast.update(kind, 'Payment sent', { variant: 'success' })   // resolves in place
 */

type ToastOptions = {
    kind?: string
    durationMs?: number
    testId?: string
    actionLabel?: string
    cancelLabel?: string
    onAction?: () => void
    detail?: string
}

function show(message: string, variant: GlobalNoticeVariant, options: ToastOptions = {}): void {
    useStore.getState().showGlobalNotice(message, { variant, ...options })
}

export const toast = {
    success: (message: string, options?: ToastOptions) => show(message, 'success', options),
    error: (message: string, options?: ToastOptions) =>
        show(message, 'error', { durationMs: 9000, ...options }),
    info: (message: string, options?: ToastOptions) => show(message, 'info', options),
    /** Persistent in-flight toast. Pass a stable `kind`, then resolve it with toast.update(). */
    pending: (message: string, options: ToastOptions & { kind: string }) =>
        show(message, 'pending', options),
    /** Resolve (or re-message) the current toast matching `kind`, in place. */
    update: (
        kind: string,
        message: string,
        options?: {
            variant?: GlobalNoticeVariant
            durationMs?: number
            testId?: string
            detail?: string
            actionLabel?: string
            cancelLabel?: string
            onAction?: () => void
        },
    ) => useStore.getState().updateGlobalNotice(kind, message, options),
}

export default toast
